/**
 * Reconnaître l'erreur qui **tue l'arbre React** (#1745).
 *
 * React retire, insère et remplace des nœuds pendant la phase de **commit** — après le rendu, une
 * fois les décisions prises. Si le parent d'un nœud a disparu du DOM entre-temps, l'appel part sur
 * `null` :
 *
 * ```
 * Uncaught TypeError: Cannot read properties of null (reading 'removeChild')
 * ```
 *
 * Trois choses en découlent, et c'est leur conjonction qui rend le défaut si mauvais :
 *
 * - la frontière `AppError` **ne la voit pas** — elle n'attrape que les erreurs de rendu ;
 * - l'arbre est mort : plus aucun rendu ne repartira, et ce qui était peint reste peint. Un
 *   chargeur affiché juste avant la bascule tourne alors indéfiniment ;
 * - rien ne s'affiche. La personne conclut à une panne, ou recharge d'elle-même.
 *
 * Qui retire le parent ? Une extension de navigateur qui injecte dans `<body>`, une traduction
 * automatique qui réécrit le texte (#1732), un portail dont le conteneur part avant ses enfants.
 * On ne sait pas laquelle ici — mais on reconnaît **l'erreur**, et c'est ce qui autorise à agir :
 * `AssetRecovery` recharge parce qu'il sait exactement ce qu'il traite, et c'est désormais le cas.
 */

/**
 * Les trois opérations de commit, et la forme que prend leur échec.
 *
 * Chrome et Firefox disent « Cannot read properties of null », Safari « null is not an object » :
 * le nom de la méthode est la seule part stable, on ancre donc dessus.
 */
const COMMIT_OPERATIONS = ['removeChild', 'insertBefore', 'appendChild', 'replaceChild'];

/**
 * Cette erreur a-t-elle tué l'arbre ?
 *
 * Deux conditions, et les deux comptent. Le nom d'une opération de commit **et** la marque d'un
 * accès sur rien : `element.removeChild(x)` peut échouer pour d'autres raisons — un nœud qui n'est
 * pas un enfant, par exemple — et ces échecs-là ne laissent pas l'arbre mort.
 *
 * `NotFoundError` est la variante que lève le DOM lui-même quand React retire un nœud qu'un tiers
 * a déjà déplacé : même conséquence, même traitement.
 */
export function isDeadTreeError(message: string | undefined | null): boolean {
  if (!message) return false;
  if (!COMMIT_OPERATIONS.some(op => message.includes(op))) return false;
  return (
    /null|undefined/.test(message) ||
    message.includes('NotFoundError') ||
    message.includes('not a child')
  );
}

type DeadTreeContext = {
  message: string;
  stack?: string;
  now: number;
  session: Pick<Storage, 'getItem' | 'setItem'> | null;
  path: string;
  version: string;
  sinceLoadMs: number;
};

export type DeadTreeOutcome =
  | { action: 'reload'; level: 'warn'; label: string; meta: Record<string, unknown> }
  | { action: 'give-up'; level: 'error'; label: string; meta: Record<string, unknown> };

/**
 * Que faire d'un arbre mort — décidé **ici**, exécuté par l'appelant (#1745).
 *
 * Recharger, une fois. Le refus est le cas important : il dit que le rechargement n'a pas suffi,
 * donc que la cause survit à la page — et c'est cette trace-là qui mènera à elle.
 *
 * Une saisie en cours est perdue ; elle l'était déjà. Un arbre mort ne reçoit plus aucun
 * événement : ce qui reste à l'écran n'est qu'une image.
 */
export function reviveOnDeadTree(
  ctx: DeadTreeContext,
  claim: (now: number, session: DeadTreeContext['session'], key: string) => boolean,
  key: string,
): DeadTreeOutcome {
  const meta = {
    message: ctx.message,
    stack: ctx.stack,
    path: ctx.path,
    version: ctx.version,
    /*
      Depuis combien de temps la page vit. « Au démarrage » et « après vingt minutes » n'ont pas
      les mêmes causes : la première accuse l'hydratation, la seconde ce qui a modifié le DOM
      entre-temps. Sans ce repère, les deux se lisent pareil dans les journaux.
    */
    sinceLoadMs: ctx.sinceLoadMs,
  };
  return claim(ctx.now, ctx.session, key)
    ? { action: 'reload', level: 'warn', label: '[dead-tree] arbre mort, rechargement', meta }
    : {
        action: 'give-up',
        level: 'error',
        label: '[dead-tree] arbre mort, rechargement déjà tenté',
        meta,
      };
}
